import mapboxgl from 'mapbox-gl'
import { useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { clearSession } from "./utils/auth";
import DataSets from './DataSets';
import {CreateProject, MenuItem, MapContainer, RealTimeData, FilterOptions, DataDistribution, SocialMediaIcons, DataTypeSelection} from './components';
import FacilityPanel from './components/FacilityPanel';
import SwitchProject from './SwitchProject';
import VisualAnalytics from './VisualAnalytics';
import HelpSupport from "./HelpSupport.js";
import CaseStudy from './components/CaseStudy';
import Workshop, { WORKSHOP_PAGES } from './components/Workshop';
import { DEFAULT_SERVICE_LEVEL } from './utils/serviceLevels';
import AnalysisResultPage from './components/AnalysisResultPage';
import EditReportPage from './components/EditReportPage';
import logo from './icons/app_logo.png';
import userAvatar from './icons/user_avatar.png';
import cornellC from './icons/cornell_c.png';
import ap from './icons/side_bar/active_project.svg'
import np from './icons/side_bar/new_project.svg'
import sp from './icons/side_bar/switch_project.svg'
import ds from './icons/side_bar/data_sets.svg'
import aoi from './icons/side_bar/areas_of_interest.svg'
import bl from './icons/side_bar/background_layer.svg'
import oda from './icons/side_bar/od_analytics.svg'
import ta from './icons/side_bar/trend_analytics.svg'
import va from './icons/side_bar/visual_analytics.svg'
import companylogo from './icons/utech-logo.png'
import 'mapbox-gl/dist/mapbox-gl.css';
import './Home.css';
import { FaBell, FaCaretDown, FaBars } from 'react-icons/fa';
import { getMapboxToken, isLiveSimulation, getDemoMetro, isDemo } from './config';
import { MOCK_MANHATTAN, fetchDatasets, fetchEditableDatasets, fetchHexagonTraffic, fetchPolygonTraffic, fetchRouteTraffic, atHour } from './utils/trafficDataset';
import { loadMetro, metroHealth } from './utils/networkEditor';
// mapboxgl.accessToken is set in index.js from /api/config, before React mounts.

// The metro the edit service should hold for a dataset, or null. A metro offered
// only as pre-computed scenarios is too large for the service, and asking for
// it would only produce a refusal.
const serviceCbsa = (ds) =>
  (isLiveSimulation() && ds?.cbsa && ds.prebaked?.live !== false ? String(ds.cbsa) : null);

function Home() {
  // A demo host shows one metro and only the parts of the app the demo uses.
  const demo = isDemo();
  // Check if this is an analysis result view opened in a new tab
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('view') === 'analysis-result') {
    return <AnalysisResultPage />;
  }
  if (urlParams.get('view') === 'edit-report') {
    return <EditReportPage />;
  }

  const [searchedPolygon, setSearchedPolygon] = useState(null);
  const [trafficLayerData, setTrafficLayerData] = useState(null);
  const [selectedMenuItem, setSelectedMenuItem] = useState("Active Project");
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedNavItem, setSelectedNavItem] = useState("Dashboard");
  // Origin-Destination Analytics: trips into the zone ('flows'), regional roads ('od') or
  // the roads around the charging zone ('pricing').
  const [odView, setOdView] = useState("flows");
  // Scenario 5's service level, one for the pricing panel and the workshop pages.
  const [serviceLevel, setServiceLevel] = useState(DEFAULT_SERVICE_LEVEL);
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [accountOpen, setAccountOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // A demo host's page fits the window: the map takes the height the layout leaves it.
  const demoFit = demo && !isFullscreen;
  const [searchDimension, setSearchDimension] = useState(null);
  const [showShortestPath, setShowShortestPath] = useState(false);
  const [routeData, setRouteData] = useState(null);
  const [routeSummary, setRouteSummary] = useState(null);
  const [showFacilities, setShowFacilities] = useState(false);
  const [facilityData, setFacilityData] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [showMacroAnalysis, setShowMacroAnalysis] = useState(false);
  const [showSchoolZones, setShowSchoolZones] = useState(false);
  const [showCongestionPricing, setShowCongestionPricing] = useState(false);
  const [showNetworkEditor, setShowNetworkEditor] = useState(false);
  const [showPublicTransport, setShowPublicTransport] = useState(false);
  const [showIncome, setShowIncome] = useState(false);
  const [showTransitInvestment, setShowTransitInvestment] = useState(false);
  const facilityFetchRef = useRef(null); // to track latest fetch and avoid stale updates

  // --- draggable map height ---------------------------------------------------
  // The divider resizes the map FRAME; the camera is deck.gl's and is untouched.
  // Height reaches the map as a CSS custom property so the 100vh/120vh rules still win.
  const MAP_MIN_H = 260;
  const [mapHeight, setMapHeight] = useState(500);
  const [isResizingMap, setIsResizingMap] = useState(false);
  const mapResizeRef = useRef(null);

  // Leave room for the header and a strip of the controls below, so the divider
  // can never be dragged past the bottom of the window and lost.
  const maxMapHeight = () => Math.max(MAP_MIN_H, window.innerHeight - 200);
  const clampMapHeight = (h) => Math.min(maxMapHeight(), Math.max(MAP_MIN_H, h));

  const startMapResize = (e) => {
    e.preventDefault(); // stop the drag from becoming a text selection
    mapResizeRef.current = { startY: e.clientY, startH: mapHeight };
    setIsResizingMap(true);
  };

  const nudgeMapHeight = (e) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    e.preventDefault();
    const step = e.shiftKey ? 50 : 10;
    setMapHeight((h) => clampMapHeight(h + (e.key === 'ArrowDown' ? step : -step)));
  };

  // The drag is tracked on a full-viewport overlay mounted only while resizing: it owns
  // the pointer, which keeps the drag alive over the deck.gl canvas.
  const moveMapResize = (e) => {
    const d = mapResizeRef.current;
    if (d) setMapHeight(clampMapHeight(d.startH + (e.clientY - d.startY)));
  };

  const endMapResize = () => {
    if (!isResizingMap) return;
    setIsResizingMap(false);
    // deck.gl and mapbox-gl each observe their own container, so the canvas
    // keeps up during the drag on its own. This just settles the final size in
    // one go instead of leaving it to whenever the next animation frame lands.
    window.dispatchEvent(new Event('resize'));
  };

  // Which traffic dataset the map is showing. Held here rather than in
  // MapContainer because both the picker (on the map) and the Traffic Density
  const [datasets, setDatasets] = useState([MOCK_MANHATTAN]);
  const [activeDataset, setActiveDataset] = useState(MOCK_MANHATTAN);

  // The hour of the day the map shows, for a run with several. School Zones and
  // the Network Editor of a pre-computed metro exist at one hour only and hold the
  // map there while they are open; closing them gives back the hour picked.
  const [activeHour, setActiveHour] = useState(null);
  useEffect(() => { setActiveHour(activeDataset?.hour ?? null); }, [activeDataset]);
  const editsHour = activeDataset?.prebaked?.editsHour ?? null;
  const hourLock = editsHour !== null && (showSchoolZones || showNetworkEditor) ? editsHour : null;
  const viewHour = hourLock ?? activeHour;

  useEffect(() => {
    // Also the cheapest liveness check on the session: a 401 here is turned into
    // a trip back to sign-in by the interceptor in utils/auth.
    fetch('/api/userflow/me')
      .then((r) => (r.ok ? r.json() : null))
      .then(setAccount)
      .catch(() => setAccount(null));
  }, []);

  const signOut = () => {
    clearSession();
    navigate('/', { replace: true });
  };

  // The edit service's state for the selected metro: what the progress strip and
  // the Network Editor read. Null for a dataset with no metro behind it.
  const [metroStatus, setMetroStatus] = useState(null);
  const metroPoll = useRef(null);
  const metroAsk = useRef(null);
  const publishSeen = useRef(null);

  // A metro whose V/C layer has just been loaded into the serving layer becomes
  // a produced run: take it as the active dataset so the layer is drawn. Same
  // metro, so nothing re-asks the service and the map does not fly again.
  const adoptPublished = useCallback((cbsa) => {
    fetchDatasets().then((list) => {
      if (!list.length) return;
      setDatasets(list);
      const published = list.find((d) => d.kind === 'run' && String(d.cbsa) === String(cbsa));
      if (published) setActiveDataset(published);
      fetchEditableDatasets(list).then((extra) => {
        setDatasets((cur) => [...cur.filter((d) => d.kind !== 'mock'), ...extra,
                              ...cur.filter((d) => d.kind === 'mock')]);
      });
    });
  }, []);

  // Ask the service to hold this metro, then follow it to baseline_ready and,
  // for a metro with no V/C layer yet, through publishing one. A metro the
  // service does not serve fails here and only the editor cares.
  const ensureMetro = useCallback((cbsa) => {
    if (metroPoll.current) { clearTimeout(metroPoll.current); metroPoll.current = null; }
    if (metroAsk.current) { clearTimeout(metroAsk.current); metroAsk.current = null; }
    if (!cbsa) { setMetroStatus(null); return; }
    // A resident metro answers /load in milliseconds. Claiming 'loading' before
    // it answers flashes the lock overlay on every switch back to one.
    metroAsk.current = setTimeout(() => {
      setMetroStatus({ cbsa, status: 'loading', step: 'requesting' });
    }, 200);
    const settle = () => { if (metroAsk.current) { clearTimeout(metroAsk.current); metroAsk.current = null; } };
    const waiting = (st) => st.status === 'loading' || st.status === 'warm'
      || st.publish?.status === 'pending';
    publishSeen.current = null;
    const apply = (st) => {
      const was = publishSeen.current;
      publishSeen.current = st.publish?.status || null;
      setMetroStatus({ cbsa, ...st });
      if (was === 'pending' && st.publish?.status === 'done') adoptPublished(cbsa);
      if (waiting(st)) follow();
    };
    const follow = () => {
      metroPoll.current = setTimeout(() => {
        metroHealth(cbsa)
          .then(({ metro, publish }) => {
            if (!metro) return setMetroStatus({ cbsa, status: 'failed', error: 'Simulation service is not reachable.' });
            apply({ ...metro, publish });
          })
          .catch(() => setMetroStatus({ cbsa, status: 'failed', error: 'Simulation service is not reachable.' }));
      }, 1500);
    };
    loadMetro(cbsa)
      .then((st) => {
        settle();
        apply(st);
      })
      .catch((err) => {
        settle();
        setMetroStatus({ cbsa, status: 'failed', error: err.message });
      });
  }, [adoptPublished]);

  useEffect(() => () => {
    if (metroPoll.current) clearTimeout(metroPoll.current);
    if (metroAsk.current) clearTimeout(metroAsk.current);
  }, []);

  useEffect(() => {
    let cancelled = false;
    // ?cbsa=<id> opens on that metro; a demo host on its own; otherwise the first run.
    const asked = new URLSearchParams(window.location.search).get('cbsa') || getDemoMetro();
    const pick = (list) => {
      const d = asked && list.find((x) => String(x.cbsa) === String(asked));
      if (!d) return false;
      setActiveDataset(d);
      ensureMetro(serviceCbsa(d));
      return true;
    };
    fetchDatasets().then((list) => {
      if (cancelled || !list.length) return;
      setDatasets(list);
      if (!pick(list)) {
        setActiveDataset(list[0]);
        ensureMetro(serviceCbsa(list[0]));
      }
      // The editable-only metros come second: the edit service is not in the
      // path of the map's own data, and must not delay it.
      fetchEditableDatasets(list).then((extra) => {
        if (cancelled || !extra.length) return;
        setDatasets((cur) => [...cur.filter((d) => d.kind !== 'mock'), ...extra,
                              ...cur.filter((d) => d.kind === 'mock')]);
        pick(extra);
      });
    });
    return () => { cancelled = true; };
  }, [ensureMetro]);

  const handleSelectDataset = useCallback((ds) => {
    setActiveDataset(ds);
    setTrafficLayerData(null);
    setRouteData(null);
    setRouteSummary(null);
    ensureMetro(serviceCbsa(ds));
  }, [ensureMetro]);

  // A metro the service is still parsing cannot be edited yet; a dataset with no
  // produced run has no V/C layer at all, so the layers that read one are off.
  const noBaselineLayer = activeDataset?.kind === 'editable';
  const baselineReason = noBaselineLayer
    ? 'no produced run for this metro'
    : null;
  // The sample tables are not a network: there is nothing for the editor to edit.
  const editorReason = activeDataset?.cbsa ? null : 'pick a metro first';

  // Without live simulation a panel exists only where its scenarios were computed
  // ahead: the Manhattan case study, or a metro with a pre-computed pack.
  const live = isLiveSimulation();
  const hideEditor = !live && !activeDataset?.prebaked;
  const hidePolicies = !live && activeDataset?.kind !== 'mock' && !activeDataset?.prebaked;
  useEffect(() => {
    if (hideEditor) setShowNetworkEditor(false);
    if (hidePolicies) {
      setShowSchoolZones(false);
      setShowCongestionPricing(false);
      setShowTransitInvestment(false);
    }
  }, [hideEditor, hidePolicies]);

  // The transit, income and transit-investment files cover the San Francisco Bay Area only.
  const bayArea = String(activeDataset?.cbsa) === '41860';
  useEffect(() => {
    if (!bayArea) {
      setShowPublicTransport(false);
      setShowIncome(false);
      setShowTransitInvestment(false);
    }
  }, [bayArea]);

  // Picking a metro locks the map until `baseline_ready`: `warm` only means the
  // network is parsed, and a /run issued before the baseline finishes queues
  // behind it inside the service. A failed service still leaves a produced run
  // usable, so only a metro that has nothing else behind it stays locked.
  // Publishing the V/C layer extends the same wait; a publish that fails does
  // not lock anything — everything but the layer still works.
  const metroName = activeDataset && String(activeDataset.label).split(' — ')[0];
  const metroReady = metroStatus?.status === 'baseline_ready'
    && metroStatus.publish?.status !== 'pending';
  const metroLocked = !!metroStatus && !!activeDataset?.cbsa
    && metroStatus.cbsa === String(activeDataset.cbsa)
    && !metroReady
    && (metroStatus.status !== 'failed' || showNetworkEditor || noBaselineLayer);
  const lockReason = metroLocked
    ? (metroStatus.status === 'failed' ? `${metroName} could not be loaded`
                                       : `${metroName} is still loading`)
    : null;
  const retryMetro = useCallback(() => {
    ensureMetro(serviceCbsa(activeDataset));
  }, [activeDataset, ensureMetro]);

  const handleSearch = (Polygon, dimension) => {
    setSearchedPolygon(Polygon);
    setSearchDimension(dimension);
  };

  const handleMenuSelect = (menuLabel) => {
    setSelectedMenuItem(menuLabel);
  };

  const handleNavSelect = (navItem) => {
    setSelectedNavItem(navItem);
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(prev => !prev);
  };

  const toggleShortestPath = () => {
    setShowShortestPath(prev => !prev);
    if (showShortestPath) {
      clearRoute(); // Clear route data when closing
    }
  };

  const closeShortestPath = () => {
    setShowShortestPath(false);
    clearRoute();
  };

  function clearRoute() {
    setRouteData(null);
    setRouteSummary(null);
  }

  const MAPBOX_TOKEN = getMapboxToken();
  const POI_CATEGORIES = ['restaurant', 'cafe', 'supermarket', 'hospital', 'pharmacy', 'school', 'gas_station', 'parking_lot', 'atm', 'hotel'];

  const toggleMacroAnalysis = () => {
    setShowMacroAnalysis(prev => !prev);
  };

  const closeMacroAnalysis = () => {
    setShowMacroAnalysis(false);
  };

  const toggleSchoolZones = () => {
    setShowSchoolZones(prev => !prev);
  };

  const toggleCongestionPricing = () => {
    setShowCongestionPricing(prev => !prev);
  };

  const toggleNetworkEditor = () => {
    setShowNetworkEditor(prev => !prev);
  };

  const toggleTransitInvestment = () => {
    setShowTransitInvestment(prev => !prev);
  };

  const toggleIncome = () => {
    setShowIncome(prev => !prev);
  };

  const togglePublicTransport = () => {
    setShowPublicTransport(prev => !prev);
  };

  const toggleFacilities = () => {
    if (showFacilities) {
      setShowFacilities(false);
      setFacilityData(null);
      setSelectedFacility(null);
    } else {
      setShowFacilities(true);
      // MapContainer will trigger fetchFacilitiesByBounds via onViewportBoundsChange
    }
  };

  const fetchFacilitiesByBounds = useCallback(async (bounds) => {
    if (!bounds) {
      setFacilityData(null);
      return;
    }

    const fetchId = Date.now();
    facilityFetchRef.current = fetchId;

    const bbox = `${bounds.west},${bounds.south},${bounds.east},${bounds.north}`;

    try {
      const allFeatures = [];
      for (const category of POI_CATEGORIES) {
        const url = `https://api.mapbox.com/search/searchbox/v1/category/${category}?access_token=${MAPBOX_TOKEN}&bbox=${bbox}&limit=25&language=en`;
        const response = await fetch(url);
        if (facilityFetchRef.current !== fetchId) return; // stale request
        if (response.ok) {
          const data = await response.json();
          if (data.features) {
            data.features.forEach(f => {
              f.properties._category = category;
            });
            allFeatures.push(...data.features);
          }
        }
      }
      if (facilityFetchRef.current === fetchId) {
        setFacilityData(allFeatures);
      }
    } catch (err) {
      console.error('Facility fetch error:', err);
    }
  }, []);

  // The last route asked for, so a new hour can annotate it again; transit leaves
  // at that hour too.
  const lastRoute = useRef(null);
  const routeAsk = useRef(0);
  const fetchRoute = async ({ startCoord, endCoord, costing }, hour) => {
    const ask = ++routeAsk.current;
    // Annotate only motorised routes: sidewalks, cycle paths and transit legs are not
    // in the SUMO network.
    const ANNOTATABLE = ['auto', 'motor_scooter'];
    if (activeDataset?.kind === 'run' && ANNOTATABLE.includes(costing)) {
      const data = await fetchRouteTraffic(atHour(activeDataset, hour), startCoord, endCoord, costing);
      if (ask !== routeAsk.current) return;
      setRouteData(data);
      setRouteSummary({ ...data.summary, trip: data.trip, segments: data.segments,
                        hour: data.run?.hour ?? null });
    } else {
      const response = await axios.post(
        "/api/link/links/byRoute/trafficDensity",
        { startCoord, endCoord, costing, hour }
      );
      if (ask !== routeAsk.current) return;
      setRouteData(response.data);
    }
    lastRoute.current = { startCoord, endCoord, costing, hour };
    setSearchDimension("route");
  };

  const handleRouteSearch = async (startCoord, endCoord, costing) => {
    // Clear route if either coordinate is null
    if (!startCoord || !endCoord || !costing) {
      routeAsk.current += 1;
      lastRoute.current = null;
      setRouteData(null);
      setRouteSummary(null);
      return;
    }
    // Clear previous route before fetching new one
    setRouteData(null);
    setRouteSummary(null);
    try {
      await fetchRoute({ startCoord, endCoord, costing }, viewHour);
    } catch (err) {
      console.error("Route API error: ", err);
    }
  };

  const [showTrafficPanel, setShowTrafficPanel] = useState(false);

  const handleDataTypeClick = (type) => {
    if (type === 'Traffic Density') setShowTrafficPanel(true);
  };

  // The last cells or area asked for, so a new hour can colour them again.
  const lastDensity = useRef(null);
  const densityAsk = useRef(0);
  const [trafficHour, setTrafficHour] = useState(null);
  const fetchDensity = async ({ mode, usable }, hour) => {
    const ask = ++densityAsk.current;
    const ds = atHour(activeDataset, hour);
    const data = mode === 'hex'
      ? await fetchHexagonTraffic(ds, usable)
      : await fetchPolygonTraffic(ds, Object.values(usable)[0]);
    if (ask !== densityAsk.current) return;
    setTrafficLayerData(data);
    setTrafficHour(ds?.hour ?? null);
  };

  const runTrafficDensity = async ({ mode, rings }) => {

    const usable = {};
    for (const [id, ring] of Object.entries(rings || {})) {
      if (Array.isArray(ring) && ring.length >= 4) usable[id] = ring;
    }
    if (Object.keys(usable).length === 0) {
      console.warn('Traffic Density: nothing selected');
      return;
    }
    setSearchDimension(mode === 'hex' ? 'hexagon' : 'link');
    lastDensity.current = { mode, usable };
    try {
      await fetchDensity(lastDensity.current, viewHour);
    } catch (err) {
      console.error('Traffic Density fetch failed:', err);
    }
  }

  // A new hour colours the same cells, and annotates the same route, again.
  useEffect(() => {
    if (viewHour === null) return;
    if (lastDensity.current && trafficLayerData && trafficHour !== viewHour) {
      fetchDensity(lastDensity.current, viewHour)
        .catch((err) => console.error('Traffic Density fetch failed:', err));
    }
    if (lastRoute.current && routeData && lastRoute.current.hour !== viewHour) {
      fetchRoute(lastRoute.current, viewHour)
        .catch((err) => console.error('Route API error: ', err));
    }
  }, [viewHour]);
  // The panels that size themselves by --map-height follow the height the layout gives
  // the map, rather than the dragged one.
  const dashboardRef = useRef(null);
  useEffect(() => {
    if (!demoFit) return undefined;
    const map = dashboardRef.current && dashboardRef.current.querySelector('.map-container');
    if (!map || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => {
      const h = Math.round(entry.contentRect.height);
      if (h > 0) setMapHeight(h);
    });
    ro.observe(map);
    return () => ro.disconnect();
  }, [demoFit]);

  // Check if the dashboard (map view) should be visible
  // San Francisco congestion-pricing pages, by the sidebar entry that opens each.
  const workshopPage = WORKSHOP_PAGES.find((p) => p.menu === selectedMenuItem)?.id;
  const isDashboardVisible = selectedNavItem === "Dashboard" && !workshopPage && !["New Project", "Switch Project", "Data Sets", "Visual Analytics", "Case Study: Manhattan"].includes(selectedMenuItem);

  const dashboardStyle = {
    display: isDashboardVisible ? (demoFit ? 'flex' : 'block') : 'none',
    '--map-height': `${mapHeight}px`,
  };

  // Render overlay content (non-map views)
  const renderOverlayContent = () => {
    if (selectedMenuItem === "New Project") {
      return <CreateProject navigateToSwitchProject={() => setSelectedMenuItem("Switch Project")} />;
    } else if (selectedMenuItem === "Switch Project") {
      return <SwitchProject />;
    } else if (selectedMenuItem === "Data Sets") {
      return <DataSets />;
    } else if (selectedMenuItem === "Visual Analytics") {
      return <VisualAnalytics key={selectedMenuItem} />
    } else if (selectedMenuItem === "Case Study: Manhattan") {
      return <CaseStudy />;
    } else if (workshopPage && selectedNavItem === "Dashboard") {
      return (
        <Workshop
          page={workshopPage}
          onNavigate={handleMenuSelect}
          odView={odView}
          onOdView={setOdView}
          serviceLevel={serviceLevel}
          onServiceLevel={setServiceLevel}
        />
      );
    }

    if (selectedNavItem === "Help & Support") {
      return (
        <div className="help-support-container">
          <h2>Help & Support Content</h2>
          <p>Help and support resources will be displayed here.</p>
          <HelpSupport />
        </div>
      );
    }

    return null;
  };

  const renderContent = () => {
    const overlayContent = renderOverlayContent();

    return (
      <>
        <div ref={dashboardRef} className={`dashboard-content ${isFullscreen ? 'fullscreen' : ''}`} style={dashboardStyle}>
          <div id="tooltip"></div>
          <MapContainer
            onSearch={handleSearch}
            trafficData={trafficLayerData}
            datasets={datasets}
            activeDataset={activeDataset}
            onSelectDataset={handleSelectDataset}
            routeData={routeData}
            routeSummary={routeSummary}
            isFullscreen={isFullscreen}
            toggleSidebar={toggleSidebar}
            toggleFullscreen={toggleFullscreen}
            showShortestPath={showShortestPath}
            onRouteSearch={handleRouteSearch}
            onCloseShortestPath={closeShortestPath}
            facilityData={facilityData}
            onSelectFacility={setSelectedFacility}
            showFacilities={showFacilities}
            onViewportBoundsChange={fetchFacilitiesByBounds}
            showMacroAnalysis={showMacroAnalysis}
            onCloseMacroAnalysis={closeMacroAnalysis}
            showSchoolZones={showSchoolZones}
            onCloseSchoolZones={() => setShowSchoolZones(false)}
            showCongestionPricing={showCongestionPricing}
            onCloseCongestionPricing={() => setShowCongestionPricing(false)}
            showTrafficPanel={showTrafficPanel}
            onCloseTrafficPanel={() => setShowTrafficPanel(false)}
            onRunTrafficDensity={runTrafficDensity}
            onTrafficResult={setTrafficLayerData}
            showNetworkEditor={showNetworkEditor}
            onCloseNetworkEditor={() => setShowNetworkEditor(false)}
            showPublicTransport={showPublicTransport}
            onTogglePublicTransport={togglePublicTransport} showIncome={showIncome} onToggleIncome={toggleIncome} showTransitInvestment={showTransitInvestment} onToggleTransitInvestment={toggleTransitInvestment} onCloseTransitInvestment={toggleTransitInvestment}
            metroStatus={metroStatus}
            metroLocked={metroLocked}
            onRetryMetro={retryMetro}
            viewHour={viewHour}
            hourLock={hourLock}
            onSelectHour={setActiveHour}
            trafficHour={trafficHour}
            serviceLevel={serviceLevel}
            onServiceLevel={setServiceLevel}
          />
          {!showNetworkEditor && !isFullscreen && !demoFit && (
            <div
              className={`map-resizer ${isResizingMap ? 'dragging' : ''}`}
              role="separator"
              aria-orientation="horizontal"
              aria-label="Resize map"
              aria-valuenow={mapHeight}
              tabIndex={0}
              title="Drag to resize the map"
              onMouseDown={startMapResize}
              onKeyDown={nudgeMapHeight}
            />
          )}
          {isResizingMap && (
            <div
              className="map-resize-overlay"
              onMouseMove={moveMapResize}
              onMouseUp={endMapResize}
              onMouseLeave={endMapResize}
            />
          )}
          {!showNetworkEditor && (
          <DataTypeSelection
            onSelectDataType={handleDataTypeClick}
            disabledReason={baselineReason}
            editorReason={editorReason}
            lockReason={lockReason}
            hideEditor={hideEditor}
            hidePolicies={hidePolicies}
            bayArea={bayArea}
            showShortestPath={showShortestPath}
            onToggleShortestPath={toggleShortestPath}
            showFacilities={showFacilities}
            onToggleFacilities={toggleFacilities}
            showMacroAnalysis={showMacroAnalysis}
            onToggleMacroAnalysis={toggleMacroAnalysis}
            showSchoolZones={showSchoolZones}
            onToggleSchoolZones={toggleSchoolZones}
            showCongestionPricing={showCongestionPricing}
            onToggleCongestionPricing={toggleCongestionPricing}
            showNetworkEditor={showNetworkEditor}
            onToggleNetworkEditor={toggleNetworkEditor}
            showPublicTransport={showPublicTransport}
            onTogglePublicTransport={togglePublicTransport} showIncome={showIncome} onToggleIncome={toggleIncome} showTransitInvestment={showTransitInvestment} onToggleTransitInvestment={toggleTransitInvestment} onCloseTransitInvestment={toggleTransitInvestment}
          />
          )}
          {selectedFacility && (
            <FacilityPanel
              facility={selectedFacility}
              onClose={() => setSelectedFacility(null)}
            />
          )}
          {/* Mock filters and charts: not on a demo host. */}
          {!showNetworkEditor && !demo && <FilterOptions />}
          {!showNetworkEditor && !demo && <DataDistribution />}
        </div>
        {overlayContent}
      </>
    );
  };

  return (
      <div className={`app ${isSidebarCollapsed ? 'sidebar-collapsed' : ''} ${isFullscreen ? 'fullscreen-app' : ''} ${showNetworkEditor ? 'network-editor-active' : ''} ${demoFit ? 'demo-fit' : ''}`}>
        <header className="header">
          <div className="logo">
            <img src={logo} alt="Logo Placeholder" />
          </div>
          <div className="title"></div>
          <nav className="top-nav">
            <a
                className={selectedNavItem === "Dashboard" ? "nav-item-selected" : ""}
                onClick={() => handleNavSelect("Dashboard")}
            >
              Dashboard
            </a>
            <a
                className={selectedNavItem === "Help & Support" ? "nav-item-selected" : ""}
                onClick={() => handleNavSelect("Help & Support")}
            >
              Help & Support
            </a>
          </nav>
          <div className="account">
            <button className="notification-button">
              <FaBell size={24} color="#7CC644" />
            </button>
            <div className="user-profile" onClick={() => setAccountOpen((o) => !o)}>
              {/* A demo host's one account is Cornell's: the "C" wordmark for everyone. */}
              <img src={isDemo() ? cornellC : userAvatar} alt="User Profile"
                   className={`user-avatar ${isDemo() ? 'user-avatar-mark' : ''}`} />
              <FaCaretDown size={16} color="#7CC644" />
              {accountOpen && (
                <div className="account-menu" onClick={(e) => e.stopPropagation()}>
                  <div className="account-menu-who">
                    <div className="account-menu-name">
                      {account
                        ? [account.first_name, account.last_name].filter(Boolean).join(' ')
                          || account.email
                        : 'Signed in'}
                    </div>
                    <div className="account-menu-email">{account?.email}</div>
                    {account?.auth_provider === 'Google' && (
                      <div className="account-menu-provider">via Google</div>
                    )}
                  </div>
                  <button className="account-menu-signout" onClick={signOut}>Sign out</button>
                </div>
              )}
            </div>
          </div>
        </header>

        <aside className={`sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}>
          <div className="section-header-with-toggle">
            {!isSidebarCollapsed && <h2>Project Management</h2>}
            <button className="toggle-sidebar-btn" onClick={toggleSidebar}>
              <FaBars size={20} color="#7CC644" />
            </button>
          </div>

          <div className="menu-section">
            <MenuItem
                label="Active Project"
                icon={ap}
                isSelected={selectedMenuItem === "Active Project"}
                onSelect={handleMenuSelect}
                isCollapsed={isSidebarCollapsed}
            />
            <MenuItem
                label="New Project"
                disabled={demo}
                icon={np}
                isSelected={selectedMenuItem === "New Project"}
                onSelect={handleMenuSelect}
                isCollapsed={isSidebarCollapsed}
            />
            <MenuItem
                label="Switch Project"
                disabled={demo}
                icon={sp}
                isSelected={selectedMenuItem === "Switch Project"}
                onSelect={handleMenuSelect}
                isCollapsed={isSidebarCollapsed}
            />
          </div>

          {!isSidebarCollapsed && <hr />}

          {!isSidebarCollapsed && (
              <div className="sidebar-title">
                <h2>Analytics Tools</h2>
              </div>
          )}

          <div className="menu-section">
            <MenuItem
                label="Visual Analytics"
                disabled={demo}
                icon={va}
                isSelected={selectedMenuItem === "Visual Analytics"}
                onSelect={handleMenuSelect}
                isCollapsed={isSidebarCollapsed}
            />
            <MenuItem
                label="Trend Analytics"
                disabled={demo}
                icon={ta}
                isSelected={selectedMenuItem === "Trend Analytics"}
                onSelect={handleMenuSelect}
                isCollapsed={isSidebarCollapsed}
            />
            <MenuItem
                label="Origin-Destination Analytics"
                icon={oda}
                isSelected={selectedMenuItem === "Origin-Destination Analytics"}
                onSelect={handleMenuSelect}
                isCollapsed={isSidebarCollapsed}
            />
          </div>

          {!isSidebarCollapsed && <hr />}

          {!isSidebarCollapsed && (
              <div className="sidebar-title">
                <h2>Scenario Analysis</h2>
              </div>
          )}

          <div className="menu-section">
            <MenuItem
                label="Scenario Comparison"
                icon={va}
                isSelected={selectedMenuItem === "Scenario Comparison"}
                onSelect={handleMenuSelect}
                isCollapsed={isSidebarCollapsed}
            />
          </div>

          {!isSidebarCollapsed && <hr />}

          {!isSidebarCollapsed && (
              <div className="sidebar-title">
                <h2>Resource Access</h2>
              </div>
          )}

          <div className="menu-section">
            <MenuItem
                label="Data Sets"
                disabled={demo}
                icon={ds}
                isSelected={selectedMenuItem === "Data Sets"}
                onSelect={handleMenuSelect}
                isCollapsed={isSidebarCollapsed}
            />
            <MenuItem
                label="Areas of Interest"
                disabled={demo}
                icon={aoi}
                isSelected={selectedMenuItem === "Areas of Interest"}
                onSelect={handleMenuSelect}
                isCollapsed={isSidebarCollapsed}
            />
            <MenuItem
                label="Background Layer"
                disabled={demo}
                icon={bl}
                isSelected={selectedMenuItem === "Background Layer"}
                onSelect={handleMenuSelect}
                isCollapsed={isSidebarCollapsed}
            />
            <MenuItem
                label="Case Study: Manhattan"
                disabled={demo}
                icon={aoi}
                isSelected={selectedMenuItem === "Case Study: Manhattan"}
                onSelect={handleMenuSelect}
                isCollapsed={isSidebarCollapsed}
            />
          </div>

          <div className={`sidebar-logo ${isSidebarCollapsed ? 'collapsed' : ''}`}>
            <img src={companylogo} alt="Company Logo" />
          </div>
        </aside>
        <main className="content_home">

          {renderContent()}

        </main>

        <footer className="footer">
          <div className='footer-content'>
            <a>About Us</a>
            <a>Privacy Policy</a>
            <a>Terms of Service</a>
          </div>
          <SocialMediaIcons />
        </footer>
      </div>
  );
}


export default Home;